import {AnimatePresence, motion} from 'framer-motion';
import {Action, Actions, IconButton} from 'platform/components';
import {Box, Clickable, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {DOT_CHARACTER, Nullish, suffixTestId, TestIdProps, useBoolean, useToggle} from 'shared/web';

import {DragHandle} from './DragHandle';

type DraggableCardVariant = 'inlineGrey' | 'inlineWhite';

interface DraggableCardProps extends TestIdProps {
  title: string;
  defaultExpanded?: boolean;
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
  variant?: DraggableCardVariant;
  subtitle?: string | Nullish;
  actions?: Action[];
  children?: ReactNode;
  additionalContent?: ReactNode;
  /**
   * Full-width content under the header row, e.g. a warning that should not push the actions.
   * Part of the header, so on an expandable card a click on it also toggles the card.
   */
  footer?: ReactNode;
  /** Keeps the actions on the right of the title; the title and content wrap instead. */
  shouldKeepActionsInline?: boolean;
  isDimmed?: boolean;
}

const DIMMED_OPACITY = 0.6;

const HEIGHT_ANIMATION_VARIANTS = {
  visible: {
    height: 'auto',
    transition: {
      duration: 0.3,
      ease: [0.3, 0.1, 0.75, 0.9] as const,
    },
    transformOrigin: 'top',
  },
  hidden: {
    height: 0,
    transition: {
      duration: 0.25,
      ease: [0.3, 0.1, 0.75, 0.9] as const,
    },
    transformOrigin: 'top',
  },
};

const OPACITY_ANIMATION_VARIANTS = {
  visible: {
    opacity: 1,
    transition: {
      delay: 0.15,
      duration: 0.15,
    },
    transformOrigin: 'top',
  },
  hidden: {
    opacity: 0,
    transition: {
      delay: 0,
      duration: 0.1,
    },
    transformOrigin: 'top',
  },
};

const FLEX_GROW_STYLE = {flexGrow: 1};
const FULL_HEIGHT_STYLE = {height: '100%'};

export function DraggableCard(props: DraggableCardProps) {
  const [isExpanded, toggleIsExpanded] = useToggle(props.defaultExpanded);
  const [isHeaderHovered, onHeaderMouseEnter, onHeaderMouseLeave] = useBoolean();

  const isVariantWhite = props.variant === 'inlineWhite';
  const hasDragHandle = Boolean(props.handleProps);
  const isExpandable = isNotNil(props.children);
  // Lets the title column shrink below its longest word so long titles wrap instead of
  // pushing the inline actions out of the card.
  const inlineActionsMinWidth = props.shouldKeepActionsInline ? 0 : undefined;

  return (
    <Box
      borderRadius="medium"
      backgroundColor={isVariantWhite ? 'palettes.white.10.100' : 'palettes.neutral.10.100'}
      opacity={props.isDimmed ? DIMMED_OPACITY : undefined}
      border="1px solid"
      borderColor="general.separator"
      data-testid={suffixTestId('draggableCardWrapper', props)}
    >
      <VStack>
        <ClickableHeader
          onHeaderClick={toggleIsExpanded}
          isExpandable={isExpandable}
          onHeaderMouseEnter={onHeaderMouseEnter}
          onHeaderMouseLeave={onHeaderMouseLeave}
        >
          <Box padding={4}>
            <VStack spacing={3}>
              <HStack spacing={3} align="center">
                <Show when={hasDragHandle}>
                  <StopPropagationWrapper>
                    <DragHandle
                      {...props.handleProps}
                      aria-label={i18n.t('general.actions.reorder')}
                    >
                      <Icon value="action/drag_indicator" color="palettes.neutral.60.100" />
                    </DragHandle>
                  </StopPropagationWrapper>
                </Show>

                <VStack spacing={1} width="100%" minWidth={inlineActionsMinWidth}>
                  {/* Centers a lone title row against the taller action buttons. With additional
                      content, the stretched title column keeps the title on the first line. */}
                  <HStack
                    justify="space-between"
                    align={isNil(props.additionalContent) ? 'center' : undefined}
                    spacing={props.shouldKeepActionsInline ? 3 : undefined}
                    wrap={!props.shouldKeepActionsInline}
                  >
                    <VStack spacing={2} shrink={1} minWidth={inlineActionsMinWidth}>
                      <HStack spacing={2} align="center">
                        <Heading
                          size={4}
                          overflowWrap={props.shouldKeepActionsInline ? 'anywhere' : undefined}
                          data-testid={suffixTestId('cardHeadingTitle', props)}
                        >
                          {props.title}
                        </Heading>
                        <Show when={Boolean(props.subtitle)}>
                          <Heading size={4}>{DOT_CHARACTER}</Heading>
                          <Text
                            size="base"
                            data-testid={suffixTestId('cardHeadingSubtitle', props)}
                          >
                            {props.subtitle}
                          </Text>
                        </Show>
                      </HStack>
                      <Show when={isNotNil(props.additionalContent)}>
                        {props.additionalContent}
                      </Show>
                    </VStack>
                    <HStack spacing={2} align="center" shrink={0}>
                      <StopPropagationWrapper>
                        <Actions
                          actions={props.actions}
                          size="default"
                          data-testid={suffixTestId('cardHeaderActions', props)}
                        />
                      </StopPropagationWrapper>

                      <Show when={isExpandable}>
                        <IconButton
                          size="small"
                          priority={isHeaderHovered ? 'secondary' : 'default'}
                          icon={isExpanded ? 'navigation/expand_less' : 'navigation/expand_more'}
                          data-testid={suffixTestId('cardHeaderExpandButton', props)}
                        />
                      </Show>
                    </HStack>
                  </HStack>
                </VStack>
              </HStack>

              {/* A footer that renders nothing leaves no gap, so a `Show` can be passed as is. */}
              {props.footer}
            </VStack>
          </Box>
        </ClickableHeader>

        <Show when={isNotNil(props.children)}>
          <AnimatePresence initial={false}>
            {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
            {isExpanded ? (
              <motion.div
                key="cardBody"
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={HEIGHT_ANIMATION_VARIANTS}
                style={FLEX_GROW_STYLE}
              >
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={OPACITY_ANIMATION_VARIANTS}
                  style={FULL_HEIGHT_STYLE}
                >
                  <Box
                    flex={1}
                    paddingBottom={4}
                    paddingHorizontal={4}
                    data-testid={suffixTestId('cardBody', props)}
                  >
                    {props.children}
                  </Box>
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </Show>
      </VStack>
    </Box>
  );
}

const StopPropagationWrapper = (props: {children: ReactNode}) => (
  <div onClick={(e) => e.stopPropagation()}>{props.children}</div>
);

interface ClickableHeaderProps {
  onHeaderClick: () => void;
  onHeaderMouseEnter: () => void;
  onHeaderMouseLeave: () => void;
  children: ReactNode;
  isExpandable?: boolean;
}

function ClickableHeader(props: ClickableHeaderProps) {
  if (!props.isExpandable) {
    return props.children;
  }

  return (
    <Clickable
      onClick={props.onHeaderClick}
      onMouseEnter={props.onHeaderMouseEnter}
      onMouseLeave={props.onHeaderMouseLeave}
    >
      {props.children}
    </Clickable>
  );
}
